import { FlexColumn, FlexRow, MaterialIcon, StyledSearchBar, useDebouncedSearch } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { OnChangeParams } from 'baseui/select';
import { memo, useMemo } from 'react';
import { useRecoilState, useResetRecoilState, useSetRecoilState } from 'recoil';
import { ORDER_ASCENDING, ORDER_DESCENDING } from '../../constants/app';
import { JORNEY_SORT_OPTIONS, JOURNEY_STATUSES } from '../../constants/journeys';
import { useStyles } from '../../hooks/useStyles';
import {
  filterStatusState,
  orderDirectionState,
  searchJourneyState,
  sortJourneysState,
  updateJourneysState,
} from '../../storage/journeys';
import { JourneySortOption } from '../../types/journey';
import { InlineSelect } from '../ui/InlineSelect';
import { SortPicker } from '../ui/SortPicker';
import { StyledTooltip } from '../ui/StyledTooltip';

function JourneyFilters() {
  const { theme, css } = useStyles();
  const { search, setSearch } = useDebouncedSearch(useRecoilState(searchJourneyState));
  const [filterStatus, setFilterStatus] = useRecoilState(filterStatusState);
  const [sort, setSort] = useRecoilState(sortJourneysState);
  const [orderDirection, setOrderDirection] = useRecoilState(orderDirectionState);
  const resetFilterStatus = useResetRecoilState(filterStatusState);
  const resetSort = useResetRecoilState(sortJourneysState);
  const resetOrderDirection = useResetRecoilState(orderDirectionState);
  const resetSearch = useResetRecoilState(searchJourneyState);
  const setUpdateJourneys = useSetRecoilState(updateJourneysState);

  const statusOptions = useMemo(() => {
    return [
      {
        id: undefined,
        label: 'Todos',
      },
      ...JOURNEY_STATUSES.map((status) => ({ id: status.type, label: status.name })),
    ];
  }, []);

  const onSortChange = (params: OnChangeParams) => {
    params.option?.id ? setSort(params.option.id as JourneySortOption) : resetSort();
  };

  const onStatusChange = (params: OnChangeParams) => {
    setFilterStatus(params.option?.id ? +params.option.id : undefined);
  };

  const onOrderDirectionClick = () => {
    setOrderDirection((prev) => (prev === ORDER_ASCENDING ? ORDER_DESCENDING : ORDER_ASCENDING));
  };

  const onResetFilters = () => {
    resetSort();
    resetSearch();
    resetOrderDirection();
    resetFilterStatus();
    setSearch('');
  };

  const onUpdate = () => {
    setUpdateJourneys('update');
  };

  return (
    <FlexColumn gap={theme.sizing.scale300}>
      <FlexRow gap={theme.sizing.scale300}>
        <div className={css({ flex: 1 })}>
          <StyledSearchBar
            name="journey-search"
            value={search}
            onChange={setSearch}
            clearable
            noMargin
          />
        </div>
        <StyledTooltip
          content="Actualizar"
          showArrow={false}
        >
          <Button
            size="compact"
            shape="square"
            onClick={onUpdate}
            kind="tertiary"
          >
            <MaterialIcon name="sync" />
          </Button>
        </StyledTooltip>
      </FlexRow>
      <FlexRow
        gap={theme.sizing.scale300}
        classNames={css({ flexWrap: 'wrap' })}
      >
        <SortPicker
          orderDirection={orderDirection}
          onSortChange={onSortChange}
          onOrderDirectionClick={onOrderDirectionClick}
          options={JORNEY_SORT_OPTIONS}
          value={sort}
        />
        <InlineSelect
          label="Estatus:"
          options={statusOptions}
          value={[{ id: filterStatus }]}
          onChange={onStatusChange}
        />
        <StyledTooltip
          content={'Restaurar filtros'}
          showArrow={false}
        >
          <Button
            size="compact"
            shape="square"
            onClick={onResetFilters}
            kind="tertiary"
          >
            <MaterialIcon name="recycling" />
          </Button>
        </StyledTooltip>
      </FlexRow>
    </FlexColumn>
  );
}

export default memo(JourneyFilters);
